import * as React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { cn } from '../lib/utils';

export interface MarkdownContentProps {
  content: string;
  className?: string;
}

// (ui ADR-001), (ui ADR-002), (app-shell ADR-005)
export const MarkdownContent = React.memo(function MarkdownContent({
  content,
  className,
}: MarkdownContentProps) {
  return (
    <div
      className={cn(
        'text-[length:var(--reading-size)] leading-[var(--reading-leading)] text-copy',
        className,
      )}
      data-testid="markdown-content"
    >
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          h1: ({ children }) => (
            <h1 className="mt-7 mb-2 first:mt-0 text-[1.45em] font-semibold leading-snug tracking-[-0.01em] text-foreground">
              {children}
            </h1>
          ),
          h2: ({ children }) => (
            <h2 className="mt-6 mb-2 first:mt-0 text-[1.25em] font-semibold leading-snug text-foreground">
              {children}
            </h2>
          ),
          h3: ({ children }) => (
            <h3 className="mt-5 mb-1.5 first:mt-0 text-[1.1em] font-semibold text-foreground">
              {children}
            </h3>
          ),
          h4: ({ children }) => (
            <h4 className="mt-5 mb-1.5 first:mt-0 text-[0.9em] font-semibold uppercase tracking-wider text-copy-muted">
              {children}
            </h4>
          ),
          h5: ({ children }) => (
            <h5 className="mt-5 mb-1.5 first:mt-0 text-[0.9em] font-semibold uppercase tracking-wider text-copy-muted">
              {children}
            </h5>
          ),
          h6: ({ children }) => (
            <h6 className="mt-5 mb-1.5 first:mt-0 text-[0.9em] font-semibold uppercase tracking-wider text-copy-muted">
              {children}
            </h6>
          ),
          p: ({ children }) => <p className="mb-4 last:mb-0">{children}</p>,
          ul: ({ children }) => (
            <ul className="mb-4 list-disc pl-5 space-y-1.5 marker:text-copy-muted">{children}</ul>
          ),
          ol: ({ children }) => (
            <ol className="mb-4 list-decimal pl-5 space-y-1.5 marker:text-copy-muted">{children}</ol>
          ),
          li: ({ children }) => (
            <li className="leading-[1.6] [&>ul]:mt-1 [&>ul]:mb-0 [&>ol]:mt-1 [&>ol]:mb-0 [&>p]:mb-0">
              {children}
            </li>
          ),
          blockquote: ({ children }) => (
            <blockquote className="my-4 border-l-2 border-border pl-3 text-copy-muted">{children}</blockquote>
          ),
          hr: () => <hr className="my-6 border-t border-border" />,
          strong: ({ children }) => <strong className="font-semibold text-foreground">{children}</strong>,
          em: ({ children }) => <em className="italic">{children}</em>,
          pre: ({ children }) => (
            <pre className="my-4 overflow-x-auto rounded-md bg-muted p-3 text-[0.85em] leading-[1.55]">
              {children}
            </pre>
          ),
          code: ({ children, className: codeClassName }) => {
            const isBlock = codeClassName?.startsWith('language-');
            if (isBlock) return <code className={codeClassName}>{children}</code>;
            return <code className="rounded bg-muted px-1 py-0.5 text-[0.9em] font-medium">{children}</code>;
          },
          a: ({ children, href }) => (
            <a
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary underline underline-offset-2 decoration-primary/40 hover:decoration-primary"
            >
              {children}
            </a>
          ),
          table: ({ children }) => (
            <div className="overflow-x-auto">
              <table className="my-4 w-full border-collapse text-[0.9em]">{children}</table>
            </div>
          ),
          th: ({ children }) => (
            <th className="border-b border-border px-3 py-2 text-left font-semibold text-copy-muted">
              {children}
            </th>
          ),
          td: ({ children }) => (
            <td className="border-b border-border/40 px-3 py-2 align-top">{children}</td>
          ),
          img: ({ src, alt }) => <img src={src} alt={alt} className="my-4 max-w-full rounded" />,
        }}
      >
        {content}
      </ReactMarkdown>
    </div>
  );
});
